import React, { useState, useEffect, useCallback } from 'react';
import Card from '../../components/common/Card/Card';
import Table from '../../components/common/Table/Table';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getAllProjects } from '../../services/projectService';
import { getClassesByProject } from '../../services/classService';
import { getAllMentors } from '../../services/mentorService';
import { getAllStudents } from '../../services/studentService';
import { formatDate, formatTime } from '../../utils/helpers';
import styles from './Classes.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const Classes = () => {
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  const filterProjects = useCallback(() => {
    let filtered = [...allProjects];

    if (selectedMentor) {
      filtered = filtered.filter(p => p.mentor_id === selectedMentor);
    }

    if (selectedStudent) {
      filtered = filtered.filter(p => p.student_id === selectedStudent);
    }

    setFilteredProjects(filtered);

    // Reset project selection if current project is not in filtered list
    if (selectedProject && !filtered.find(p => p.id === selectedProject)) {
      setSelectedProject('');
    }
  }, [allProjects, selectedMentor, selectedStudent, selectedProject]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [filterProjects]);

  useEffect(() => {
    if (selectedProject) {
      fetchClasses(selectedProject);
    } else {
      setClasses([]);
    }
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      const [projectsData, mentorsData, studentsData] = await Promise.all([
        getAllProjects(),
        getAllMentors(),
        getAllStudents()
      ]);
      setAllProjects(projectsData);
      setFilteredProjects(projectsData);
      setMentors(mentorsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async (projectId) => {
    try {
      const data = await getClassesByProject(projectId);
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const columns = [
    { header: 'Class #', accessor: 'class_number' },
    { header: 'Date', accessor: 'class_date', render: (row) => formatDate(row.class_date) },
    { header: 'Time', accessor: 'class_time', render: (row) => formatTime(row.class_time) },
    { header: 'Duration (min)', accessor: 'duration' },
    { header: 'Topics', accessor: 'topics_covered', render: (row) => row.topics_covered?.join(', ') || 'N/A' },
    { header: 'Mentor', render: (row) => row.mentor_attendance ? 'Yes' : 'No' },
    { header: 'Student', render: (row) => row.student_attendance ? 'Yes' : 'No' },
    { header: 'Bot', render: (row) => row.bot_attendance ? 'Yes' : 'No' },
  ];

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.classesPage}>
      <h1>Classes</h1>

      <div className={styles.filterSection}>
        <div className={styles.formGroup}>
          <label>Select Mentor</label>
          <select value={selectedMentor} onChange={(e) => setSelectedMentor(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {mentors.map(mentor => (
              <option key={mentor.id} value={mentor.id}>
                {mentor.first_name} {mentor.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Select Student</label>
          <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {students.map(student => (
              <option key={student.id} value={student.id}>
                {student.first_name} {student.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Select Project</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {sortProjectsByName(filteredProjects).map(project => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProject ? (
        <Card>
          <Table columns={columns} data={classes} />
        </Card>
      ) : (
        <Card>
          <p className={styles.empty}>Please select a project to view classes</p>
        </Card>
      )}
    </div>
  );
};

export default Classes;